import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { App, Button, Card, DatePicker, Form, InputNumber, Modal, Select, Space, Table, Tag, Typography, } from 'antd';
import { CheckOutlined, DollarOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { api, apiError } from '@/lib/api';
import { formatDate, formatRupiah } from '@/lib/format';
import { useAuthStore } from '@/lib/auth-store';
const STATUS_COLORS = {
    UNPAID: 'red',
    PARTIAL: 'gold',
    PAID: 'green',
};
export function MaintenanceSchedulesPage() {
    const { message } = App.useApp();
    const qc = useQueryClient();
    const hasPerm = useAuthStore((s) => s.hasPerm);
    const [statusFilter, setStatusFilter] = useState();
    const [accrualModal, setAccrualModal] = useState(null);
    const [payModal, setPayModal] = useState(null);
    const [accrualForm] = Form.useForm();
    const [payForm] = Form.useForm();
    const q = useQuery({
        queryKey: ['makam-maintenance', statusFilter],
        queryFn: async () => (await api.get('/makam/maintenance', {
            params: { status: statusFilter },
        })).data,
    });
    const accrueMut = useMutation({
        mutationFn: async ({ id, body }) => (await api.post(`/makam/maintenance/${id}/accrue`, body)).data,
        onSuccess: () => {
            message.success('Iuran diakru & jurnal diposting');
            qc.invalidateQueries({ queryKey: ['makam-maintenance'] });
            setAccrualModal(null);
        },
        onError: (e) => message.error(apiError(e)),
    });
    const payMut = useMutation({
        mutationFn: async ({ id, body }) => (await api.post(`/makam/maintenance/${id}/pay`, body)).data,
        onSuccess: () => {
            message.success('Pembayaran dicatat');
            qc.invalidateQueries({ queryKey: ['makam-maintenance'] });
            setPayModal(null);
        },
        onError: (e) => message.error(apiError(e)),
    });
    const now = dayjs();
    const cols = [
        { title: 'Kontrak', dataIndex: 'contract_no', width: 200 },
        { title: 'Pembeli', dataIndex: 'buyer_name' },
        { title: 'Petak', dataIndex: 'plot_code', width: 120 },
        { title: 'Tahun', dataIndex: 'year', width: 90 },
        {
            title: 'Nilai',
            dataIndex: 'amount_due',
            width: 150,
            align: 'right',
            render: (v) => _jsx("span", { className: "money", children: formatRupiah(v) }),
        },
        {
            title: 'Terbayar',
            dataIndex: 'paid_amount',
            width: 150,
            align: 'right',
            render: (v) => _jsx("span", { className: "money", children: formatRupiah(v) }),
        },
        {
            title: 'Outstanding',
            dataIndex: 'outstanding',
            width: 150,
            align: 'right',
            render: (v) => _jsx("span", { className: "money", children: _jsx("b", { children: formatRupiah(v) }) }),
        },
        {
            title: 'Jatuh Tempo',
            dataIndex: 'due_date',
            width: 130,
            render: (d) => {
                const days = now.diff(dayjs(d), 'day');
                return (_jsxs(Space, { direction: "vertical", size: 0, children: [_jsx("span", { children: formatDate(d) }), days > 0 ? _jsxs(Tag, { color: "red", children: ["Overdue ", days, "h"] }) : null] }));
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (s) => _jsx(Tag, { color: STATUS_COLORS[s], children: s }),
        },
        {
            title: 'Aksi',
            width: 220,
            render: (_, r) => hasPerm('makam.contract:approve') ? (_jsxs(Space, { children: [!r.accrual_journal_id && (_jsx(Button, { size: "small", icon: _jsx(CheckOutlined, {}), onClick: () => {
                            accrualForm.setFieldsValue({
                                amount_due: parseFloat(r.amount_due) || 500000,
                                due_date: dayjs(r.due_date),
                            });
                            setAccrualModal(r);
                        }, children: "Akru" })), r.accrual_journal_id && r.status !== 'PAID' && (_jsx(Button, { size: "small", type: "primary", icon: _jsx(DollarOutlined, {}), onClick: () => {
                            payForm.setFieldsValue({
                                amount: parseFloat(r.outstanding),
                                tx_date: dayjs(),
                            });
                            setPayModal(r);
                        }, children: "Bayar" }))] })) : null,
        },
    ];
    return (_jsxs("div", { children: [_jsxs(Space, { style: { marginBottom: 16, display: 'flex', justifyContent: 'space-between', width: '100%' }, children: [_jsx(Typography.Title, { level: 3, style: { margin: 0 }, children: "Iuran Perawatan Makam" }), _jsx(Select, { allowClear: true, style: { width: 180 }, placeholder: "Filter status", value: statusFilter, onChange: (v) => setStatusFilter(v), options: Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s })) })] }), _jsx(Card, { children: _jsx(Table, { rowKey: "id", loading: q.isLoading, dataSource: q.data ?? [], columns: cols, pagination: { pageSize: 30 }, size: "middle" }) }), _jsx(Modal, { title: `Akru iuran ${accrualModal?.contract_no ?? ''} — ${accrualModal?.year ?? ''}`, open: !!accrualModal, onCancel: () => setAccrualModal(null), onOk: () => accrualForm.validateFields().then((v) => {
                    if (!accrualModal)
                        return;
                    accrueMut.mutate({
                        id: accrualModal.id,
                        body: {
                            amount_due: String(v.amount_due),
                            due_date: v.due_date.format('YYYY-MM-DD'),
                        },
                    });
                }), confirmLoading: accrueMut.isPending, children: _jsxs(Form, { form: accrualForm, layout: "vertical", children: [_jsx(Form.Item, { name: "amount_due", label: "Nilai iuran (Rp)", rules: [{ required: true }], children: _jsx(InputNumber, { style: { width: '100%' }, min: 0, formatter: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'), parser: ((v) => parseFloat((v ?? '').replace(/\./g, '')) || 0) }) }), _jsx(Form.Item, { name: "due_date", label: "Jatuh Tempo", rules: [{ required: true }], children: _jsx(DatePicker, { style: { width: '100%' } }) })] }) }), _jsx(Modal, { title: `Bayar iuran ${payModal?.contract_no ?? ''} — ${payModal?.year ?? ''}`, open: !!payModal, onCancel: () => setPayModal(null), onOk: () => payForm.validateFields().then((v) => {
                    if (!payModal)
                        return;
                    payMut.mutate({
                        id: payModal.id,
                        body: {
                            amount: String(v.amount),
                            tx_date: v.tx_date.format('YYYY-MM-DD'),
                        },
                    });
                }), confirmLoading: payMut.isPending, children: _jsxs(Form, { form: payForm, layout: "vertical", children: [_jsx(Form.Item, { name: "amount", label: "Nominal Pembayaran (Rp)", rules: [{ required: true }], children: _jsx(InputNumber, { style: { width: '100%' }, min: 0, formatter: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'), parser: ((v) => parseFloat((v ?? '').replace(/\./g, '')) || 0) }) }), _jsx(Form.Item, { name: "tx_date", label: "Tanggal Bayar", rules: [{ required: true }], children: _jsx(DatePicker, { style: { width: '100%' } }) })] }) })] }));
}
